import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Icon, naira, nairaK, HeroLineChart, NetworkDonut, APP_LOGO, APP_LOGO_WHITE } from "./lib/core";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const partnerApi = axios.create({ baseURL: `${BACKEND_URL}/api/partner`, withCredentials: true });
partnerApi.interceptors.request.use((config) => {
  const t = localStorage.getItem("topit_partner_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-blue-100 text-blue-700",
  paid: "bg-green-100 text-green-700",
  reversed: "bg-slate-100 text-slate-500 line-through",
};

const NAV = [
  { id: "refer", label: "Refer & Earn", icon: "campaign" },
  { id: "customers", label: "Customers", icon: "groups" },
  { id: "transactions", label: "Transactions", icon: "receipt_long" },
  { id: "analytics", label: "Analytics", icon: "monitoring" },
];

const BUSINESS_TYPES = [
  "POS Agent", "Distributor", "Church", "Gym", "Football Academy",
  "Betting Company", "Community Association", "University Union/Student Association",
  "Phone Accessory shop", "Pharmacy", "Supermarket", "Others",
];

const AVATAR_COLORS = ["bg-red-500", "bg-blue-500", "bg-emerald-500", "bg-violet-500", "bg-amber-500", "bg-pink-500", "bg-cyan-500"];
const initials = (n) => (n || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

// ==================== AUTH ====================
const PartnerAuth = ({ onAuthed }) => {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({
    email: "", password: "", phone: "",
    business_name: "", business_reg_no: "", business_tin: "",
    director_name: "", director_nin: "", gov_id_number: "",
    business_address: "", business_type: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  // Email verification step
  const [verifying, setVerifying] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState("");
  const [verifyOtp, setVerifyOtp] = useState("");
  const [verifyInfo, setVerifyInfo] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const errText = (d) => (typeof d === "string" ? d : "Something went wrong");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (mode === "login") {
        const r = await partnerApi.post("/login", { email: form.email, password: form.password });
        localStorage.removeItem("topit_partner_token"); // session now in httpOnly cookie
        onAuthed();
      } else {
        const r = await partnerApi.post("/signup", form);
        if (r.data?.requires_verification) {
          setVerifyEmail(r.data.email || form.email);
          setVerifying(true);
        } else {
          setPending(true);
        }
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail && typeof detail === "object" && detail.code === "email_not_verified") {
        try { await partnerApi.post("/resend-verification", { email: detail.email || form.email }); } catch { /* cooldown ok */ }
        setVerifyEmail(detail.email || form.email);
        setVerifying(true);
      } else {
        setError(errText(detail));
      }
    } finally { setLoading(false); }
  };

  const verifySubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await partnerApi.post("/verify-email", { email: verifyEmail, otp: verifyOtp });
      setVerifying(false);
      setPending(true);
    } catch (err) {
      setError(errText(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const resendVerify = async () => {
    if (cooldown > 0) return;
    setError(""); setVerifyInfo("");
    try {
      await partnerApi.post("/resend-verification", { email: verifyEmail });
      setVerifyInfo("A new code has been sent to your email.");
      setCooldown(60);
    } catch (err) {
      const m = errText(err.response?.data?.detail);
      setError(m);
      const match = /(\d+)s/.exec(m);
      if (match) setCooldown(parseInt(match[1], 10));
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6" data-testid="partner-verify">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl">
          <div className="w-14 h-14 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4"><Icon name="mark_email_read" className="text-3xl" /></div>
          <h2 className="text-xl font-bold text-slate-900">Verify your email</h2>
          <p className="text-slate-500 mt-2 text-sm">Enter the 6-digit code we sent to <span className="font-semibold text-slate-700" data-testid="partner-verify-email">{verifyEmail}</span>.</p>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-xl text-sm mt-4" data-testid="partner-verify-error">{error}</div>}
          {verifyInfo && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-2.5 rounded-xl text-sm mt-4" data-testid="partner-verify-info">{verifyInfo}</div>}
          <form onSubmit={verifySubmit} className="mt-5 space-y-3">
            <input data-testid="partner-verify-otp" value={verifyOtp} onChange={(e) => setVerifyOtp(e.target.value.replace(/\D/g, ""))} maxLength={6} inputMode="numeric" placeholder="------" className="w-full h-12 px-4 text-center text-2xl font-bold tracking-[0.5em] rounded-xl border border-slate-200 outline-none focus:border-red-500" />
            <button data-testid="partner-verify-submit" type="submit" disabled={loading || verifyOtp.length !== 6} className="w-full h-11 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition-colors disabled:opacity-50">{loading ? "Verifying..." : "Verify & Continue"}</button>
          </form>
          <p className="text-sm text-slate-500 mt-4">Didn't get it?{" "}
            <button data-testid="partner-verify-resend" onClick={resendVerify} disabled={cooldown > 0} className="text-red-600 font-semibold disabled:opacity-50">{cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}</button>
          </p>
        </div>
      </div>
    );
  }

  if (pending) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6" data-testid="partner-pending">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-2xl">
          <div className="w-14 h-14 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-4"><Icon name="hourglass_top" className="text-3xl" /></div>
          <h2 className="text-xl font-bold text-slate-900">Application received</h2>
          <p className="text-slate-500 mt-2">An admin will review and approve your partner account. You'll be able to log in once approved.</p>
          <button onClick={() => { setPending(false); setMode("login"); }} className="mt-6 text-red-600 font-semibold" data-testid="partner-back-login">Back to login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="text-center mb-6">
          <img src={APP_LOGO} alt="TopIt" className="h-9 mx-auto" data-testid="partner-logo" />
          <p className="text-slate-400 text-[11px] tracking-wide mt-1.5" data-testid="powered-by-sineli">Partners · Powered by Sineli</p>
          <p className="text-slate-500 text-sm mt-3">{mode === "login" ? "Sign in to your partner dashboard" : "Apply to become a TopIt partner"}</p>
        </div>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-xl text-sm mb-4" data-testid="partner-auth-error">{error}</div>}
        <form onSubmit={submit} className="space-y-3">
          {mode === "signup" && (
            <>
              <input data-testid="partner-business-name" value={form.business_name} onChange={set("business_name")} placeholder="Business name" required className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
              <input data-testid="partner-business-reg-no" value={form.business_reg_no} onChange={set("business_reg_no")} placeholder="Business registration number" required className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
              <input data-testid="partner-business-tin" value={form.business_tin} onChange={set("business_tin")} placeholder="Business TIN" required className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
              <select data-testid="partner-business-type" value={form.business_type} onChange={set("business_type")} required className={`w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500 bg-white ${form.business_type ? "text-slate-900" : "text-slate-400"}`}>
                <option value="" disabled>Business type</option>
                {BUSINESS_TYPES.map((t) => <option key={t} value={t} className="text-slate-900">{t}</option>)}
              </select>
              <input data-testid="partner-business-address" value={form.business_address} onChange={set("business_address")} placeholder="Business address" required className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
              <input data-testid="partner-director-name" value={form.director_name} onChange={set("director_name")} placeholder="Director's name" required className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
              <input data-testid="partner-director-nin" value={form.director_nin} onChange={set("director_nin")} placeholder="Director's National Identity Number (NIN)" required className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
              <input data-testid="partner-gov-id" value={form.gov_id_number} onChange={set("gov_id_number")} placeholder="Government-issued ID number" required className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
              <input data-testid="partner-phone" value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
            </>
          )}
          <input data-testid="partner-email" type="email" value={form.email} onChange={set("email")} placeholder="Email" required className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
          <input data-testid="partner-password" type="password" value={form.password} onChange={set("password")} placeholder="Password" required className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
          <button data-testid="partner-submit" type="submit" disabled={loading} className="w-full h-11 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition-colors disabled:opacity-50">
            {loading ? "Please wait..." : mode === "login" ? "Sign in" : "Apply as partner"}
          </button>
        </form>
        <p className="text-center text-sm text-slate-500 mt-5">
          {mode === "login" ? "New partner?" : "Already have an account?"}{" "}
          <button onClick={() => { setMode(mode === "login" ? "signup" : "login"); setError(""); }} className="text-red-600 font-semibold" data-testid="partner-toggle-mode">
            {mode === "login" ? "Apply here" : "Sign in"}
          </button>
        </p>
      </div>
    </div>
  );
};

// ==================== SHARED UI ====================
const StatCard = ({ icon, label, value, tone = "slate", testid }) => {
  const tones = { slate: "bg-slate-100 text-slate-600", red: "bg-red-100 text-red-600", green: "bg-green-100 text-green-600", blue: "bg-blue-100 text-blue-600", amber: "bg-amber-100 text-amber-600" };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${tones[tone]}`}><Icon name={icon} /></div>
      <p className="text-2xl font-extrabold text-slate-900">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
};

const ShareButtons = ({ link }) => {
  const text = "Top up airtime & data on TopIt and earn rewards. Use my link:";
  const enc = encodeURIComponent;
  const targets = [
    { id: "whatsapp", label: "WhatsApp", icon: "chat", color: "bg-green-500", url: `https://wa.me/?text=${enc(text + " " + link)}` },
    { id: "telegram", label: "Telegram", icon: "send", color: "bg-sky-500", url: `https://t.me/share/url?url=${enc(link)}&text=${enc(text)}` },
    { id: "x", label: "X", icon: "tag", color: "bg-slate-900", url: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(link)}` },
    { id: "facebook", label: "Facebook", icon: "thumb_up", color: "bg-blue-600", url: `https://www.facebook.com/sharer/sharer.php?u=${enc(link)}` },
  ];
  const native = async () => {
    if (navigator.share) { try { await navigator.share({ title: "TopIt", text, url: link }); } catch { /* cancelled */ } }
    else { navigator.clipboard.writeText(link); }
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid="partner-share">
      {targets.map((t) => (
        <a key={t.id} href={t.url} target="_blank" rel="noreferrer" data-testid={`share-${t.id}`} className={`${t.color} text-white px-3 h-10 rounded-xl text-sm font-semibold flex items-center gap-1.5 hover:opacity-90 transition-opacity`}>
          <Icon name={t.icon} className="text-base" /> {t.label}
        </a>
      ))}
      <button onClick={native} data-testid="share-native" className="bg-red-600 text-white px-3 h-10 rounded-xl text-sm font-semibold flex items-center gap-1.5 hover:opacity-90"><Icon name="ios_share" className="text-base" /> Share</button>
    </div>
  );
};

const CopyBtn = ({ value, testid, label = "Copy" }) => {
  const [c, setC] = useState(false);
  return (
    <button data-testid={testid} onClick={() => { navigator.clipboard.writeText(value); setC(true); setTimeout(() => setC(false), 1500); }} className="inline-flex items-center gap-1.5 text-red-600 text-sm font-semibold hover:text-red-700">
      <Icon name={c ? "check" : "content_copy"} className="text-base" /> {c ? "Copied!" : label}
    </button>
  );
};

const Loader = () => <div className="flex justify-center py-16"><Icon name="progress_activity" className="animate-spin text-4xl text-red-600" /></div>;

// ==================== TABS ====================
const ReferEarnTab = ({ me, stats }) => {
  const code = (stats.codes && stats.codes[0]?.code) || "—";
  const link = `${window.location.origin}/?partner=${code}`;
  const e = stats.earnings || {};
  return (
    <div className="space-y-6" data-testid="tab-refer">
      {/* Hero referral card */}
      <div className="bg-slate-900 rounded-3xl p-6 text-white relative overflow-hidden">
        <div className="absolute -right-8 -top-8 w-40 h-40 bg-red-600/30 rounded-full blur-2xl" />
        <p className="text-slate-300 text-sm">Your referral code</p>
        <p className="text-4xl font-extrabold tracking-[0.2em] mt-1" data-testid="partner-code">{code}</p>
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <code className="bg-white/10 px-3 py-2 rounded-lg text-sm flex-1 truncate" data-testid="partner-link">{link}</code>
          <div className="bg-white/10 px-3 py-2 rounded-lg"><CopyBtn value={link} testid="partner-copy-link" label="Copy link" /></div>
        </div>
        <div className="mt-4"><ShareButtons link={link} /></div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon="ads_click" label="Clicks" value={stats.clicks} tone="blue" testid="stat-clicks" />
        <StatCard icon="group_add" label="Signups" value={stats.signups} tone="amber" testid="stat-signups" />
        <StatCard icon="shopping_bag" label="Sales" value={stats.sales} tone="slate" testid="stat-sales" />
        <StatCard icon="payments" label="Total Earned" value={naira(e.total)} tone="green" testid="stat-earned" />
      </div>

      {/* Earnings breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-4"><p className="text-xs text-amber-600 font-semibold uppercase">Pending</p><p className="text-xl font-bold text-slate-900 mt-1">{naira(e.pending)}</p></div>
        <div className="bg-white rounded-2xl border border-slate-200 p-4"><p className="text-xs text-blue-600 font-semibold uppercase">Approved</p><p className="text-xl font-bold text-slate-900 mt-1">{naira(e.approved)}</p></div>
        <div className="bg-white rounded-2xl border border-slate-200 p-4"><p className="text-xs text-green-600 font-semibold uppercase">Paid out</p><p className="text-xl font-bold text-slate-900 mt-1">{naira(e.paid)}</p></div>
        <div className="bg-white rounded-2xl border border-slate-200 p-4"><p className="text-xs text-slate-400 font-semibold uppercase">Reversed</p><p className="text-xl font-bold text-slate-500 mt-1">{naira(e.reversed)}</p></div>
      </div>

      {/* Commission rates */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="font-bold text-slate-900 mb-3">Your commission rates</h3>
        <div className="flex flex-wrap gap-3">
          {["airtime", "data"].map((svc) => {
            const c = (me.commission || {})[svc];
            return (
              <div key={svc} className="px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200" data-testid={`rate-${svc}`}>
                <span className="capitalize text-slate-500 text-sm">{svc}: </span>
                <span className="font-bold text-slate-900">{c ? (c.type === "percent" ? `${c.rate}%` : naira(c.rate)) : "Not set"}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CustomersTab = () => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { partnerApi.get("/customers").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <Loader />;
  const items = data.items.filter((c) => !q || c.name.toLowerCase().includes(q.toLowerCase()) || (c.phone || "").includes(q));
  return (
    <div className="space-y-4" data-testid="tab-customers">
      <div className="relative">
        <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
        <input data-testid="customers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search referred customers..." className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:border-red-500" />
      </div>
      {items.length === 0 ? (
        <div className="text-center text-slate-400 py-16">No referred customers yet. Share your link to grow your network!</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((c, i) => (
            <div key={c.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3" data-testid={`customer-${i}`}>
              <div className={`w-11 h-11 rounded-full ${AVATAR_COLORS[i % AVATAR_COLORS.length]} text-white flex items-center justify-center font-bold`}>{initials(c.name)}</div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 truncate">{c.name}</p>
                <p className="text-xs text-slate-400">{c.phone} · joined {fmtDate(c.joined)}</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-slate-900">{naira(c.commission)}</p>
                <p className="text-xs text-slate-400">{c.sales} sale{c.sales === 1 ? "" : "s"}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TransactionsTab = () => {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  useEffect(() => { partnerApi.get(`/transactions?page=${page}&limit=20`).then((r) => setData(r.data)).catch(() => {}); }, [page]);
  if (!data) return <Loader />;
  return (
    <div className="space-y-4" data-testid="tab-transactions">
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-5 py-3">Date</th>
                <th className="text-left px-3 py-3">Service</th>
                <th className="text-left px-3 py-3">Provider</th>
                <th className="text-left px-3 py-3">Customer</th>
                <th className="text-left px-3 py-3">Sale</th>
                <th className="text-left px-3 py-3">Commission</th>
                <th className="text-left px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && <tr><td colSpan="7" className="px-5 py-10 text-center text-slate-400">No transactions yet.</td></tr>}
              {data.items.map((t) => (
                <tr key={t.id} className="border-t border-slate-100">
                  <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{fmtDate(t.created_at)}</td>
                  <td className="px-3 py-3 capitalize text-slate-700">{t.service_type}</td>
                  <td className="px-3 py-3 text-slate-700">{t.provider}</td>
                  <td className="px-3 py-3 text-slate-500">{t.customer_phone}</td>
                  <td className="px-3 py-3 text-slate-500">{naira(t.sale_amount)}</td>
                  <td className="px-3 py-3 font-bold text-slate-900">{naira(t.commission_amount)}</td>
                  <td className="px-5 py-3"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_BADGE[t.status] || "bg-slate-100"}`}>{t.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {data.pages > 1 && (
        <div className="flex justify-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40">Prev</button>
          <span className="px-3 py-1.5 text-slate-500">Page {data.page} / {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
};

const AnalyticsTab = () => {
  const [data, setData] = useState(null);
  useEffect(() => { partnerApi.get("/analytics").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <Loader />;
  const s = data.summary || {};
  const e = s.earnings || {};
  const points = (data.monthly || []).map((m) => ({ amount: m.earnings }));
  const totalComm = (data.by_provider || []).reduce((a, p) => a + (p.commission || 0), 0);
  const segments = (data.by_provider || []).map((p) => ({ network: p.network, pct: p.pct }));
  return (
    <div className="space-y-6" data-testid="tab-analytics">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon="ads_click" label="Clicks" value={s.clicks} tone="blue" />
        <StatCard icon="group_add" label="Signups" value={s.signups} tone="amber" />
        <StatCard icon="shopping_bag" label="Sales" value={s.sales} tone="slate" />
        <StatCard icon="payments" label="Total Earned" value={naira(e.total)} tone="green" />
      </div>

      {/* Earnings trend */}
      <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-3xl p-6 text-white">
        <p className="text-white/80 text-sm">Earnings · last 6 months</p>
        <p className="text-3xl font-extrabold mt-1">{naira((data.monthly || []).reduce((a, m) => a + m.earnings, 0))}</p>
        <div className="mt-3"><HeroLineChart points={points.length ? points : [{ amount: 0 }, { amount: 0 }]} /></div>
        <div className="flex justify-between mt-1 text-[11px] text-white/70">
          {(data.monthly || []).map((m) => <span key={m.month}>{m.label}</span>)}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* By service */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-bold text-slate-900 mb-4">Commission by service</h3>
          <div className="space-y-3">
            {(data.by_service || []).map((b) => {
              const max = Math.max(...(data.by_service || []).map((x) => x.commission), 1);
              return (
                <div key={b.service} data-testid={`byservice-${b.service}`}>
                  <div className="flex justify-between text-sm mb-1"><span className="capitalize text-slate-600">{b.service} <span className="text-slate-400">· {b.sales} sales</span></span><span className="font-bold text-slate-900">{naira(b.commission)}</span></div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-red-500 rounded-full" style={{ width: `${(b.commission / max) * 100}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>

        {/* By provider donut */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-bold text-slate-900 mb-4">Commission by network</h3>
          <div className="flex items-center gap-5">
            <NetworkDonut segments={segments.length ? segments : [{ network: "Unknown", pct: 0 }]} total={totalComm} />
            <div className="space-y-2 flex-1">
              {(data.by_provider || []).length === 0 && <p className="text-slate-400 text-sm">No data yet.</p>}
              {(data.by_provider || []).map((p) => (
                <div key={p.network} className="flex justify-between text-sm">
                  <span className="text-slate-600">{p.network}</span>
                  <span className="font-semibold text-slate-900">{naira(p.commission)} <span className="text-slate-400">({p.pct}%)</span></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== SHELL ====================
const PartnerShell = ({ onLogout }) => {
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("refer");
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    partnerApi.get("/me").then((r) => setMe(r.data)).catch(() => onLogout());
    partnerApi.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [onLogout]);

  if (!me || !stats) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Icon name="progress_activity" className="animate-spin text-4xl text-red-600" /></div>;

  const active = NAV.find((n) => n.id === tab);
  const SidebarInner = (
    <>
      <div className="px-5 py-5 text-white" data-testid="partner-sidebar-logo">
        <img src={APP_LOGO_WHITE} alt="TopIt" className="h-7 w-auto object-contain" />
        <div className="flex items-center gap-1.5 mt-1.5">
          <span className="text-red-500 font-bold text-[11px] uppercase tracking-wide">Partners</span>
          <span className="text-slate-400 text-[9px] tracking-wide" data-testid="powered-by-sineli">· Powered by Sineli</span>
        </div>
      </div>
      <nav className="px-3 space-y-1 flex-1">
        {NAV.map((n) => (
          <button key={n.id} onClick={() => { setTab(n.id); setNavOpen(false); }} data-testid={`nav-${n.id}`}
            className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${tab === n.id ? "bg-red-600 text-white" : "text-slate-300 hover:bg-white/5"}`}>
            <Icon name={n.icon} /> {n.label}
          </button>
        ))}
      </nav>
      <div className="px-3 pb-5 border-t border-white/10 pt-3 mt-3">
        <div className="px-4 py-2"><p className="text-sm font-semibold text-white truncate">{me.company || me.name}</p><p className="text-xs text-slate-400 truncate">{me.email}</p></div>
        <button onClick={onLogout} data-testid="partner-logout" className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-300 hover:bg-white/5"><Icon name="logout" /> Logout</button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="partner-dashboard">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 bg-slate-900">{SidebarInner}</aside>
      {/* Mobile slideover */}
      {navOpen && (
        <div className="lg:hidden fixed inset-0 z-40" onClick={() => setNavOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <aside className="absolute inset-y-0 left-0 w-64 bg-slate-900 flex flex-col" onClick={(e) => e.stopPropagation()}>{SidebarInner}</aside>
        </div>
      )}

      <div className="lg:pl-64">
        {/* Top header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button className="lg:hidden text-slate-600" onClick={() => setNavOpen(true)} data-testid="partner-mobile-menu"><Icon name="menu" /></button>
              <h1 className="text-lg font-bold text-slate-900">{active?.label}</h1>
            </div>
            <div className="text-right hidden sm:block"><p className="text-sm font-semibold text-slate-900">{me.company || me.name}</p><p className="text-xs text-slate-400">Partner</p></div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-5 py-8">
          {tab === "refer" && <ReferEarnTab me={me} stats={stats} />}
          {tab === "customers" && <CustomersTab />}
          {tab === "transactions" && <TransactionsTab />}
          {tab === "analytics" && <AnalyticsTab />}
        </main>
      </div>
    </div>
  );
};

const PartnerApp = () => {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  useEffect(() => {
    // Session lives in an httpOnly cookie — validate via /me
    partnerApi.get("/me").then(() => setAuthed(true)).catch(() => {}).finally(() => setChecking(false));
  }, []);
  const logout = async () => {
    try { await partnerApi.post("/logout"); } catch { /* noop */ }
    localStorage.removeItem("topit_partner_token");
    setAuthed(false);
  };
  if (checking) return <div className="min-h-screen flex items-center justify-center bg-slate-900"><Icon name="progress_activity" className="text-red-500 text-5xl animate-spin" /></div>;
  return authed ? <PartnerShell onLogout={logout} /> : <PartnerAuth onAuthed={() => setAuthed(true)} />;
};

export default PartnerApp;
